'use client';

import { useTranslation } from 'next-i18next';
import { type ActionItem, usePromptBarActions } from './usePromptBarActions';

export interface MissionNavItem {
  item: ActionItem;
  label: string;
}

/**
 * The mission destinations in display order (Jobs, Talents) plus the shared click handler.
 * Consumed by the mobile BottomNav and the desktop HeaderMissionLinks — the two surfaces that
 * replaced the mission chips — and by the prompt bar's `+` popup.
 *
 * Bestie left the menus entirely; `/bestie-chat` is reached through the logo, which links to
 * `/` and redirects logged-in users there (see app/[locale]/(no-footer)/page.tsx).
 */
export const useMissionNavItems = (
  locale: Locale,
  isAuthenticated: boolean,
  // MyJobs+ and Add job as plain entries, appended last. On by consumers that list them
  // (the `+` popup, the mobile BottomNav); the desktop header leaves this off —
  // MainMenuLinks renders both there.
  includeMyJobs = false,
): { items: MissionNavItem[]; handleClick: (item: ActionItem) => void } => {
  const { t } = useTranslation('common');
  // The mode param is not used by usePromptBarActions — 'chat' is an arbitrary valid value.
  const { candidateItems, companyItems, handleClick } = usePromptBarActions(locale, 'chat', isAuthenticated);
  const itemsByKey = new Map([...candidateItems, ...companyItems].map((item) => [item.key, item]));

  const items = [
    { item: itemsByKey.get('jobs'), label: t('mc.chip.jobs') },
    // Talents = talent search (/cv/search, hard navigation — see usePromptBarActions).
    // Stories left the menu entirely; /stories stays reachable by direct link only.
    { item: itemsByKey.get('talents'), label: t('header.label.cv_search') },
    ...(includeMyJobs ? [
      { item: itemsByKey.get('candidate-management'), label: t('mc.chip.my.jobs') },
      { item: itemsByKey.get('add-job'), label: t('mc.chip.add') },
    ] : []),
  ].filter((entry): entry is MissionNavItem => Boolean(entry.item));

  return { items, handleClick };
};
